import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../Hooks';
import { Payout } from '../DashboardClasses';
import { Groupper, Header, Label, Placeholder, Stat, Table } from 'react-frontier';
import { useTranslation } from 'react-i18next';
import { addCommas } from '@payfront/components/Util';
import { Link } from 'react-router-dom';
import API from '../API';
import moment from 'moment';

interface PayoutBalance{
	payout_total: number,
	payout_available: number,
	pending_total: number,
	pending_available: number,
}

var PayoutList = ()=>{
	var { t } = useTranslation();
	var { user, admin, active_client } = useUser();
	var [pending, setPending] = useState<Payout[]>(null);
	var [completed, setCompleted] = useState<Payout[]>(null);
	var [balance, setBalance] = useState<PayoutBalance>(null);
	var [loadError, setLoadError] = useState<string>(null);

	useEffect(()=>{
		API.listPayouts(active_client, true, true, true).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPending(res.data.pending);
			setCompleted(res.data.completed);
			setBalance(res.data.balance);
		}).catch(err=>{
			setLoadError(t('common.unexpected_error', { code: 'PYTLST-1' }));
		});
	}, []);

	var payout_stats = useMemo(()=>{
		if(admin ? !pending : !balance) return null;
		var amount = {
			available: 0,
			total: 0,
		}
		if(admin){
			amount.available = pending.reduce((a,b)=>a+b.amount_paid, 0);
		}else{
			amount = {
				available: balance.payout_available,
				total: balance.payout_total,
			}
		}
		return amount;
	}, [pending, balance, admin]);

	if(loadError){
		return <Header text={t('common.error')} subtext={loadError} style={{ marginTop: 30 }} />
	}
	
	var collapsing = !admin ? (
		[0, 2, 3]
	) : (
		[0, 1, 3, 4]
	);

	return <div className="pf payout-list-screen" style={{ padding: 15 }}>
		<Stat.Group>
			<Stat value={(
				<Placeholder height={36} value={addCommas(payout_stats?.available)} />
			)} label={admin ? t('payout_list.stats.total_pending') : t('payout_list.stats.amount_available')} />
			<Stat value={
				<Placeholder height={36} value={addCommas(admin ? pending?.length : payout_stats?.total, !admin)} />
			} label={admin ? t('payout_list.stats.pending_payouts') : t('payout_list.stats.amount_total')} />
		</Stat.Group>
		<Groupper title={t('payout_list.title')}>
			<Table striped fitted
				className='last'
				headers={!admin ? (
					['ID', t('common.date'), t('common.status'), t('common.payout')]
				) : (
					['ID', t('common.client'), t('common.date'), t('common.status'), t('common.payout')]
				)}
			>
				{!!(pending || completed) ? <>
					{(pending && pending.length>0) && <>
						{pending.map(a=>(
							<Table.Row key={`PYPD-${a.payout_id}`} selectable compact collapsingIndexes={collapsing} as={Link} to={`/payouts/${a.payout_id}`} data={[
								a.payout_id,
								...(admin ? [a.client_name] : []),
								moment.unix(a.date_created).format('DD/MM/YY'),
								<Label color='blue'>{t('common.pending')}</Label>,
								`$${addCommas(a.amount_paid)}`
							]} />
						))}
						{!!completed && completed.length>0 && (
							<Table.Divider header text={t('payout_list.completed')} />
						)}
					</>}
					{!!completed && completed.length>0 && (
						completed.map(a=>(
							<Table.Row key={`PYCPL-${a.payout_id}`} selectable compact collapsingIndexes={collapsing} as={Link} to={`/payouts/${a.payout_id}`} data={[
								a.payout_id,
								...(admin ? [a.client_name] : []),
								moment.unix(a.date_paid || a.date_cancelled || a.date_created).format('DD/MM/YY HH:mm'),
								<Label color={a.paid ? 'green' : 'red'}>{t(`payout_list.status.${a.paid ? 'completed' : 'cancelled'}`)}</Label>,
								`$${addCommas(a.amount_paid)}`
							]} />
						))
					)}
				</> : (
					new Array(10).fill('0').map((a, i)=>(
						<Table.Row key={`PYTPLH-${i}`} data={[
							<Placeholder width={60} random={false} />,
							<Placeholder width={150} random={false} />,
							<Placeholder width={100} random={false} />,
							<Placeholder width={30} />,
						]} />
					))
				)}
			</Table>
		</Groupper>
	</div>
}

export default PayoutList;